'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import Skeleton from '@mui/material/Skeleton';
import setAlertValue from '@/components/alert';
import SvgPhone from '@/components/icons/SvgPhone';
import {
  PRIVACY_ROUTER_PATH,
  TERMS_ROUTER_PATH,
} from '@/constants/router-path';
import { baseLogin } from './baseLogin';
import { setCookie } from '@/utils/storage';
import { usePolling } from '@/utils/hooks/usePolling';
import { wechatLogin, getWechatQrcodeConfig } from '@/api/auth';

const WechatLogin = (props: {
  setIsCodeLogin: React.Dispatch<React.SetStateAction<boolean>>;
  headerTitleClassName?: string;
  subTitle?: string;
  defaultSearchParams?: URLSearchParams;
}) => {
  const {
    setIsCodeLogin,
    subTitle,
    headerTitleClassName = 'text-xl',
    defaultSearchParams,
  } = props;

  const [loginUrl, setLoginUrl] = React.useState('');
  const [sceneStr, setSceneStr] = React.useState('');

  const searchParams = useSearchParams();

  const { start, stop } = usePolling({
    fn: async () => {
      const res = await wechatLogin(sceneStr);
      if (res.status !== 200) return false;
      const user = res.data;
      if (user?.is_authenticated) {
        setCookie('user_id', user.id, 14);
        setAlertValue({
          msg: '登录成功，跳转中...',
          severity: 'success',
        });
        await baseLogin(defaultSearchParams || searchParams);
        return true;
      }
      return false;
    },
    timeout: 300000,
    onError: () => {
      setAlertValue({
        msg: '请求失败，请刷新页面并重试！',
        autoHideDuration: null,
      });
      setLoginUrl('');
      setSceneStr('');
    },
    onTimeout: () => {
      setAlertValue({
        msg: '二维码已失效，请刷新页面并重试！',
        autoHideDuration: null,
      });
      setLoginUrl('');
      setSceneStr('');
    },
  });

  React.useEffect(() => {
    if (sceneStr) {
      start();
    }
    return () => stop();
  }, [sceneStr]);

  React.useEffect(() => {
    getWechatQrcodeConfig()
      .then((res) => {
        if (res.status === 200) {
          setLoginUrl(res.data.url);
          setSceneStr(res.data.scene);
        }
      })
      .catch(() =>
        setAlertValue({
          msg: '二维码请求失败，请刷新页面并重试',
        }),
      );
  }, []);

  return (
    <>
      <h1 className={`font-semibold text-center ${headerTitleClassName}`}>
        微信登录/注册
      </h1>
      {subTitle && (
        <p className="mt-3 pb-4 text-base text-center text-web-grey-600">
          {subTitle}
        </p>
      )}
      <div className="flex justify-center items-center mt-8 w-full">
        {loginUrl ? (
          <Image src={loginUrl} alt="" width={226} height={226} />
        ) : (
          <Skeleton width={226} height={226} variant="rounded" />
        )}
      </div>
      <div className="flex justify-center my-6 tablet:my-8">
        <div
          className="flex items-center justify-center text-sm cursor-pointer hover:text-web-blue-500"
          onClick={() => setIsCodeLogin(true)}
        >
          <SvgPhone />
          <span className="ml-2">验证码登录/注册</span>
        </div>
      </div>
      <p className="text-xs leading-5 text-black/65 text-center">
        已阅读并同意
        <Link href={TERMS_ROUTER_PATH} className="text-web-blue-500">
          《用户协议》
        </Link>
        和
        <Link href={PRIVACY_ROUTER_PATH} className="text-web-blue-500">
          《隐私政策》
        </Link>
      </p>
    </>
  );
};

export default WechatLogin;
